import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { LogOut, ExternalLink, Images, Inbox } from "lucide-react";
import { api } from "@/lib/api";
import { useGallery } from "@/hooks/useGallery";
import { AdminLogin } from "@/components/admin/AdminLogin";
import { UploadZone } from "@/components/admin/UploadZone";
import { AdminPhotoGrid } from "@/components/admin/AdminPhotoGrid";
import { RequestsPanel } from "@/components/admin/RequestsPanel";

const useAdminSession = () => {
    const [user, setUser] = useState(null);
    useEffect(() => {
        localStorage.removeItem("tl_admin_token");
        api.get("/auth/me")
            .then(({ data }) => setUser(data))
            .catch(() => setUser(false));
    }, []);
    const logout = async () => {
        try { await api.post("/auth/logout"); } catch { /* ignore */ }
        setUser(false);
    };
    return { user, setUser, logout };
};

const GalleryPanel = () => {
    const { photos, setPhotos } = useGallery();
    const list = photos || [];
    return (
        <div data-testid="admin-gallery-panel">
            <UploadZone onUploaded={(p) => setPhotos((prev) => [p, ...(prev || [])])} />
            <div className="mt-12 flex items-center gap-3">
                <Images size={16} className="text-tl-orange" />
                <p data-testid="admin-photo-count" className="font-mono-tl text-[11px] uppercase tracking-[0.25em] text-white/60">
                    {list.length} foto in galleria
                </p>
            </div>
            <div className="mt-5">
                <AdminPhotoGrid photos={list} setPhotos={setPhotos} />
            </div>
        </div>
    );
};

const TABS = [
    { id: "galleria", label: "Galleria", icon: Images },
    { id: "richieste", label: "Richieste", icon: Inbox },
];

const Dashboard = ({ user, onLogout }) => {
    const [tab, setTab] = useState("galleria");
    return (
        <div data-testid="admin-dashboard">
            <div className="flex flex-col gap-6 border-b border-white/10 pb-8 md:flex-row md:items-end md:justify-between">
                <div>
                    <p className="font-mono-tl text-[11px] uppercase tracking-[0.3em] text-tl-orange">Area riservata</p>
                    <h1 className="mt-4 font-display text-3xl font-bold tracking-tight text-white sm:text-4xl">
                        Pannello TECNO-LAV<span className="text-tl-orange">.</span>
                    </h1>
                    <p data-testid="admin-user-email" className="mt-2 text-sm text-tl-mutedLight">Connesso come {user.email}</p>
                </div>
                <div className="flex flex-wrap gap-3">
                    <Link
                        to="/galleria"
                        data-testid="admin-view-gallery-link"
                        className="inline-flex items-center gap-2 border border-white/20 px-5 py-2.5 font-mono-tl text-xs uppercase tracking-[0.2em] text-white transition-colors hover:border-tl-orange hover:text-tl-orange"
                    >
                        <ExternalLink size={14} /> Vedi galleria
                    </Link>
                    <button
                        type="button"
                        data-testid="admin-logout-button"
                        onClick={onLogout}
                        className="inline-flex items-center gap-2 border border-white/20 px-5 py-2.5 font-mono-tl text-xs uppercase tracking-[0.2em] text-white transition-colors hover:border-red-400 hover:text-red-400"
                    >
                        <LogOut size={14} /> Esci
                    </button>
                </div>
            </div>
            <div className="mt-8 flex gap-1 border-b border-white/10" role="tablist">
                {TABS.map(({ id, label, icon: Icon }) => (
                    <button
                        key={id}
                        type="button"
                        role="tab"
                        aria-selected={tab === id}
                        data-testid={`admin-tab-${id}`}
                        onClick={() => setTab(id)}
                        className={`-mb-px inline-flex items-center gap-2 border-b-2 px-5 py-3 font-mono-tl text-xs uppercase tracking-[0.2em] transition-colors ${
                            tab === id ? "border-tl-orange text-white" : "border-transparent text-white/50 hover:text-white"
                        }`}
                    >
                        <Icon size={14} /> {label}
                    </button>
                ))}
            </div>
            <div className="mt-10">{tab === "galleria" ? <GalleryPanel /> : <RequestsPanel />}</div>
        </div>
    );
};

export default function Admin() {
    const { user, setUser, logout } = useAdminSession();
    return (
        <main data-testid="admin-page" className="min-h-screen bg-tl-dark2 pb-24 pt-32 md:pt-36">
            <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-12">
                {user === null && <p className="text-center text-sm text-tl-mutedLight">Verifica sessione…</p>}
                {user === false && <AdminLogin onSuccess={setUser} />}
                {user && <Dashboard user={user} onLogout={logout} />}
            </div>
        </main>
    );
}
